import React, { useState, useEffect } from 'react';
import { X, Send, Paperclip, User, UserPlus, FileText, ArrowLeft, MessageSquare, Download, Clock, CheckSquare, Eye, ListTodo, Trash2 } from 'lucide-react';
import api from '../api';

const MessageDetails = ({ message, onClose, userRole: propUserRole }) => {
  const [replyText, setReplyText] = useState('');
  const [replyPdf, setReplyPdf] = useState(null);
  const [sending, setSending] = useState(false);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveyResults, setSurveyResults] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [submittingSurvey, setSubmittingSurvey] = useState(false);

  const currentUser = sessionStorage.getItem('userEmail') || "usuario@admin.com";
  const userRole = propUserRole || sessionStorage.getItem('userRole') || "AL";

  useEffect(() => {
     if (message.survey_data) {
         if (message.sender === currentUser) {
             // The sender sees the results
             fetchSurveyResults();
         } else {
             // Everyone else (students, other teachers) can vote
             checkIfVoted();
         }
     }
  }, [message]);

  const fetchSurveyResults = async () => {
      try {
          const res = await api.get(`surveys/${message.survey_data.id}/results/`);
          setSurveyResults(res.data);
      } catch (err) {
          console.error("Error fetching survey results:", err);
      }
  };

  const checkIfVoted = async () => {
      try {
          // As an optimization, we could just check if the backend returned a flag.
          // For now, we fetch responses and see if the user has one for this survey.
          const res = await api.get(`survey-responses/?survey=${message.survey_data.id}`);
          // The backend view should ideally filter this by user, but assuming it does or we check here
          if (res.data.length > 0) {
              setHasVoted(true);
              setSurveyAnswers(res.data[0].answers_json);
          }
      } catch (err) {
          console.error("Error checking vote status:", err);
      }
  };

  const handleSurveySubmit = async () => {
      if (Object.keys(surveyAnswers).length !== message.survey_data.questions_json.length) {
          alert("Por favor responda todas las preguntas de la encuesta.");
          return;
      }
      setSubmittingSurvey(true);
      try {
          await api.post('survey-responses/', {
              survey: message.survey_data.id,
              user: sessionStorage.getItem('userId'), // assuming we have it, else backend uses request.user
              answers_json: surveyAnswers
          });
          setHasVoted(true);
          alert("¡Gracias por tu participación!");
      } catch (err) {
          console.error("Error submitting survey:", err);
          alert("Hubo un error al enviar tus respuestas.");
      } finally {
          setSubmittingSurvey(false);
      }
  };
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = [
        "application/pdf", 
        "application/msword", 
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
    if (file && allowedTypes.includes(file.type)) {
      setReplyPdf(file);
    } else {
      alert("Por favor seleccione un archivo PDF o Word (.doc, .docx)");
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return null;
    const isWord = fileName.toLowerCase().endsWith('.doc') || fileName.toLowerCase().endsWith('.docx');
    return <FileText size={16} className={`me-2 ${isWord ? 'text-primary' : 'text-danger'}`} />;
  };

  const getFileTypeLabel = (fileName) => {
    if (!fileName) return '';
    return fileName.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Word';
  };

  const onView = (pdfData, pdfName) => {
    if (!pdfData) return alert("Este archivo no puede previsualizarse.");
    const isWord = pdfName?.toLowerCase().endsWith('.doc') || pdfName?.toLowerCase().endsWith('.docx');
    if (isWord) return alert("Los documentos Word se deben descargar para ser visualizados.");
    
    const newWindow = window.open();
    newWindow.document.write(
      `<iframe src="${pdfData}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`
    );
    newWindow.document.title = pdfName || "Vista previa PDF";
  };

  const onDownload = (pdfData, pdfName) => {
    if (!pdfData) return alert("El archivo no está disponible.");
    const link = document.createElement("a");
    link.href = pdfData;
    link.download = pdfName || "documento";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
        const fd = new FormData();
        fd.append('text', replyText);
        if (replyPdf) {
            fd.append('pdf_file', replyPdf);
        }

        await api.post(`messages/${message.id}/reply/`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        setReplyText('');
        setReplyPdf(null);
        onClose(true); // Trigger refresh in parent
    } catch (error) {
        console.error('Error sending reply:', error);
        alert("Error al enviar la respuesta.");
    } finally {
        setSending(false);
    }
  };

  const handleDeleteMessage = async () => {
      if (!window.confirm("¿Estás seguro de que quieres eliminar este mensaje y su encuesta asociada? Esta acción no se puede deshacer.")) {
          return;
      }
      
      try {
          await api.delete(`messages/${message.id}/`);
          alert("Mensaje eliminado exitosamente.");
          onClose(true); // Close and refresh
      } catch (error) {
          console.error('Error deleting message:', error);
          alert("Hubo un error al eliminar el mensaje.");
      }
  };

  return (
    <div className="animate-fade-in text-start">
        <div className="d-flex align-items-center justify-content-between mb-4">
             <div className="d-flex align-items-center">
                 <button onClick={() => onClose(false)} className="btn btn-glass btn-sm me-3">
                    <ArrowLeft size={18} />
                 </button>
                 <h4 className="mb-0">Detalle del Mensaje</h4>
             </div>
             {message.sender === currentUser && (
                 <button 
                    className="btn btn-outline-danger btn-sm d-flex align-items-center"
                    onClick={handleDeleteMessage}
                    title="Eliminar mensaje y encuesta"
                 >
                     <Trash2 size={16} className="me-1" /> Eliminar
                 </button>
             )}
        </div>

        <div className="message-header glassmorphism-dark p-4 rounded-4 mb-4 border border-secondary border-opacity-25 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1">MENSAJE ORIGINAL</span>
                <div className="d-flex align-items-center gap-1 text-muted small">
                    <Clock size={14} />
                    {new Date(message.timestamp).toLocaleString()}
                </div>
            </div>
            {message.subject && (
                <div className="mb-3">
                    <h5 className="fw-bold text-primary mb-1">{message.subject}</h5>
                    <hr className="my-1 opacity-25" />
                </div>
            )}
            <div className="d-flex align-items-center mb-2">
                <User size={16} className="me-2 text-primary" />
                <span className="fw-bold">{message.sender}</span>
            </div>
            <p className="mb-2 text-dark">{message.text}</p>
            {message.pdfName && (
                <div className="d-inline-flex align-items-center p-2 bg-white border rounded">
                    {getFileIcon(message.pdfName)}
                    <span className="small me-2 fw-bold">{getFileTypeLabel(message.pdfName)}:</span>
                    <span className="small me-2 text-truncate" style={{maxWidth: '150px'}}>{message.pdfName}</span>
                    <div className="d-flex gap-2 ms-auto">
                        {!message.pdfName.toLowerCase().endsWith('.doc') && !message.pdfName.toLowerCase().endsWith('.docx') && (
                            <Eye 
                                size={14} 
                                className="cursor-pointer text-primary" 
                                title="Ver documento" 
                                onClick={() => onView(message.pdfData, message.pdfName)}
                            />
                        )}
                        <Download 
                            size={14} 
                            className="cursor-pointer text-muted" 
                            title="Descargar" 
                            onClick={() => onDownload(message.pdfData, message.pdfName)}
                        />
                    </div>
                </div>
            )}
        </div>

        <div className="replies-section mb-4">
            <label className="form-label text-muted small fw-bold mb-2">INTERACCIONES ({message.replies?.length || 0})</label>
            {message.replies?.length > 0 ? (
                message.replies.map((reply, index) => (
                    <div key={index} className={`p-2 rounded mb-2 ${reply.sender === currentUser ? 'bg-primary bg-opacity-10 border-start border-primary border-4 ms-4' : 'bg-secondary bg-opacity-10 border-start border-secondary border-4'}`}>
                        <div className="d-flex justify-content-between small mb-1 opacity-75">
                            <span className="fw-bold d-flex align-items-center gap-1">
                                <User size={12} /> {reply.sender === currentUser ? 'Mí' : reply.sender}
                            </span>
                            <span>{new Date(reply.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="mb-1">{reply.text}</p>
                        {reply.pdfName && (
                             <div className="small d-flex align-items-center gap-2 border rounded p-1 bg-white bg-opacity-50">
                                 {getFileIcon(reply.pdfName)}
                                 <span className="text-truncate" style={{maxWidth: '150px'}}>{reply.pdfName}</span>
                                 <div className="d-flex gap-2 ms-auto">
                                     {!reply.pdfName.toLowerCase().endsWith('.doc') && !reply.pdfName.toLowerCase().endsWith('.docx') && (
                                        <Eye 
                                            size={12} 
                                            className="cursor-pointer text-primary" 
                                            title="Ver respuesta" 
                                            onClick={() => onView(reply.pdfData, reply.pdfName)}
                                        />
                                     )}
                                     <Download 
                                        size={12} 
                                        className="cursor-pointer text-muted" 
                                        title="Descargar respuesta" 
                                        onClick={() => onDownload(reply.pdfData, reply.pdfName)}
                                     />
                                 </div>
                             </div>
                        )}
                    </div>
                ))
            ) : (
                <p className="text-muted small italic text-center py-2">No hay respuestas aún.</p>
            )}

            {/* Encuesta Interactiva */}
            {message.survey_data && (
                <div className="mt-4 border border-info border-opacity-25 rounded bg-white p-3 shadow-sm">
                    <div className="d-flex align-items-center mb-3 text-info">
                        <ListTodo size={18} className="me-2" />
                        <h6 className="mb-0 fw-bold">{message.survey_data.title}</h6>
                    </div>
                    {message.survey_data.description && <p className="small text-muted mb-3">{message.survey_data.description}</p>}

                    {/* VISTA PARA VOTANTES (No es el creador del mensaje) */}
                    {message.sender !== currentUser && !hasVoted && (
                        <div className="survey-form">
                            {message.survey_data.questions_json.map((q, idx) => (
                                <div key={q.id} className="mb-4 p-3 bg-light rounded shadow-sm border">
                                    <p className="fw-semibold mb-3">{idx + 1}. {q.text}</p>
                                    
                                    {(!q.type || q.type === 'radio') ? (
                                        <div className="d-grid gap-2">
                                            {q.options.map((opt, optIdx) => (
                                                <button 
                                                    key={optIdx}
                                                    type="button"
                                                    className={`btn text-start p-3 fw-medium ${surveyAnswers[q.id] === opt ? 'btn-primary shadow flex-grow-1' : 'btn-outline-secondary'}`}
                                                    onClick={() => setSurveyAnswers({...surveyAnswers, [q.id]: opt})}
                                                    style={{ transition: 'all 0.2s', borderRadius: '12px' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className={`me-3 rounded-circle d-flex align-items-center justify-content-center border ${surveyAnswers[q.id] === opt ? 'border-white bg-white' : 'border-secondary'}`} style={{ width: '22px', height: '22px' }}>
                                                            {surveyAnswers[q.id] === opt && <div className="rounded-circle bg-primary" style={{ width: '12px', height: '12px' }}></div>}
                                                        </div>
                                                        {opt}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            placeholder="Escribe tu respuesta aquí..."
                                            value={surveyAnswers[q.id] || ''}
                                            onChange={(e) => setSurveyAnswers({...surveyAnswers, [q.id]: e.target.value})}
                                            style={{ borderRadius: '10px' }}
                                        ></textarea>
                                    )}
                                </div>
                            ))}
                            <button 
                                className="btn btn-primary bg-gradient btn-lg w-100 fw-bold mt-2 shadow-sm border-0" 
                                onClick={handleSurveySubmit}
                                disabled={submittingSurvey}
                                style={{ borderRadius: '12px' }}
                            >
                                {submittingSurvey ? 'Enviando...' : 'Confirmar y Enviar Respuestas'}
                            </button>
                        </div>
                    )}

                    {/* VISTA PARA VOTANTES (Ya votó) */}
                    {message.sender !== currentUser && hasVoted && (
                        <div className="survey-voted">
                            <div className="d-flex align-items-center mb-3 p-3 bg-success bg-opacity-10 rounded text-success">
                                <CheckSquare size={20} className="me-2" />
                                <span className="mb-0 fw-bold">Gracias, ya has respondido a esta encuesta.</span>
                            </div>
                            
                            <div className="survey-form opacity-75 pe-none">
                                {message.survey_data.questions_json.map((q, idx) => (
                                    <div key={q.id} className="mb-4 p-3 bg-light rounded shadow-sm border border-success border-opacity-25">
                                        <p className="fw-semibold mb-3">{idx + 1}. {q.text}</p>
                                        
                                        {(!q.type || q.type === 'radio') ? (
                                            <div className="d-grid gap-2">
                                                {q.options.map((opt, optIdx) => (
                                                    <button 
                                                        key={optIdx}
                                                        type="button"
                                                        className={`btn text-start p-3 fw-medium ${surveyAnswers[q.id] === opt ? 'btn-success shadow flex-grow-1' : 'btn-outline-secondary'}`}
                                                        style={{ borderRadius: '12px' }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <div className={`me-3 rounded-circle d-flex align-items-center justify-content-center border ${surveyAnswers[q.id] === opt ? 'border-white bg-white' : 'border-secondary'}`} style={{ width: '22px', height: '22px' }}>
                                                                {surveyAnswers[q.id] === opt && <div className="rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></div>}
                                                            </div>
                                                            {opt}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <textarea
                                                className="form-control bg-white"
                                                rows="4"
                                                readOnly
                                                value={surveyAnswers[q.id] || ''}
                                                style={{ borderRadius: '10px' }}
                                            ></textarea>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* VISTA PARA EL EMISOR (Resultados analíticos) */}
                    {message.sender === currentUser && surveyResults && (
                        <div className="survey-results">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="small fw-bold text-muted">Feedback Analítico</span>
                                <span className="badge bg-secondary"><UserPlus size={12} className="me-1" />{surveyResults.total_responses} Respuestas</span>
                            </div>
                            
                            {Object.entries(surveyResults.questions).map(([qId, qData], idx) => (
                                <div key={qId} className="mb-4 p-3 bg-light border rounded">
                                    <p className="fw-semibold mb-3">{idx + 1}. {qData.text}</p>
                                    
                                    {(!qData.type || qData.type === 'radio') ? (
                                        qData.stats.map((stat, sIdx) => (
                                            <div key={sIdx} className="mb-3">
                                                <div className="d-flex justify-content-between small text-muted mb-1" style={{fontSize: '0.8rem'}}>
                                                    <span className="fw-medium text-dark">{stat.option}</span>
                                                    <span className="fw-bold">{stat.percentage}% ({stat.count})</span>
                                                </div>
                                                <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                                                    <div 
                                                        className="progress-bar bg-primary" 
                                                        role="progressbar" 
                                                        style={{ width: `${stat.percentage}%` }} 
                                                        aria-valuenow={stat.percentage} 
                                                        aria-valuemin="0" 
                                                        aria-valuemax="100"
                                                    ></div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-answers-list mt-2">
                                            {qData.text_answers && qData.text_answers.length > 0 ? (
                                                <div className="list-group list-group-flush border rounded overflow-hidden">
                                                    {qData.text_answers.map((ans, aIdx) => (
                                                        <div key={aIdx} className="list-group-item bg-white py-2 px-3 small">
                                                            {ans}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-muted small py-2 text-center text-italic">No hay respuestas escritas aún.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {surveyResults.total_responses === 0 && (
                                <p className="text-muted small text-center my-3">Aún no hay respuestas registradas.</p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>

        <form onSubmit={handleReply} className="border-top border-secondary border-opacity-10 pt-4 mt-2">
             <div className="d-flex align-items-center gap-2 mb-3">
                 <div className="p-2 bg-primary bg-opacity-10 rounded text-primary">
                    <Send size={16} />
                 </div>
                 <label className="form-label text-muted small fw-bold mb-0 text-uppercase tracking-wider">Enviar Respuesta</label>
             </div>
             
             <div className="form-floating mb-3">
                 <textarea 
                    className="form-control bg-dark bg-opacity-10 border-secondary border-opacity-20" 
                    placeholder="Escribe tu respuesta aquí..."
                    id="replyText"
                    style={{ height: '100px', borderRadius: '15px' }}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    required
                 ></textarea>
                 <label htmlFor="replyText" className="text-muted small">Escribe tu mensaje...</label>
             </div>
             
             <div className="d-flex gap-3 align-items-center">
                <div className="flex-grow-1">
                    <input 
                        type="file" 
                        id="reply-pdf" 
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" 
                        className="d-none"
                        onChange={handlePdfChange} 
                    />
                    <button 
                        type="button"
                        className={`btn w-100 d-flex align-items-center justify-content-center gap-2 transition-all shadow-sm ${replyPdf ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => document.getElementById('reply-pdf').click()}
                        style={{ borderRadius: '12px', padding: '10px' }}
                    >
                        {replyPdf ? (
                            <><CheckSquare size={18} /> {replyPdf.name}</>
                        ) : (
                            <><Paperclip size={18} /> Adjuntar Documento</>
                        )}
                    </button>
                </div>
                <button 
                    type="submit" 
                    className="btn btn-primary btn-lg px-5 shadow-glow transition-all d-flex align-items-center gap-2" 
                    disabled={sending}
                    style={{ borderRadius: '12px' }}
                >
                    {sending ? 'Enviando...' : <><Send size={18} /> Enviar</>}
                </button>
             </div>
        </form>
    </div>
  );
};

export default MessageDetails;
